import { ErrorHandler, Injectable, inject, provideAppInitializer } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { browserChunkReloadDeps, ChunkReloadDeps, isChunkLoadError, reloadOnceOnChunkError } from './chunk-reload';

/**
 * App-wide ErrorHandler: reloads the page once when a lazy chunk is gone after a deploy
 * (FE-7), and sends every other error to Sentry (Sentry's own Angular handler).
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    protected delegate: ErrorHandler = Sentry.createErrorHandler({ showDialog: false });
    protected deps: () => ChunkReloadDeps = browserChunkReloadDeps;
    private reloading = false;
    private lastError: unknown;

    handleError(err: unknown): void {
        if (this.reloading) {
            return;
        }
        // The same chunk error can arrive twice (NavigationError hook + router rethrow).
        if (err !== undefined && err === this.lastError) {
            return;
        }
        this.lastError = err;
        if (reloadOnceOnChunkError(err, this.deps())) {
            this.reloading = true;
            return;
        }
        this.delegate.handleError(err);
    }
}

export const SentryProvider = {
    provide: ErrorHandler,
    useClass: SentryErrorHandler
};

/** Lazy-route chunk failures surface as NavigationError; hand them to the ErrorHandler. */
export const ChunkLoadNavigationProvider = provideAppInitializer(() => {
    const router = inject(Router);
    const handler = inject(ErrorHandler);
    router.events.subscribe(e => {
        if (e instanceof NavigationError && isChunkLoadError(e.error)) {
            handler.handleError(e.error);
        }
    });
});
